/**
 * Sign In Page — monochrome minimal (Home-Screen template language),
 * Supabase-backed via the app AuthContext (email/password + Google).
 */
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { safeRedirectPath } from "@/lib/redirect";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-900 transition";

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = safeRedirectPath(
    (location.state as { from?: unknown } | null)?.from
  );
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(redirectTo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Google sign in failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#FAFAF7]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.15)] border border-neutral-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-medium tracking-tight text-neutral-900">Welcome back</h1>
            <p className="mt-2 text-neutral-500">Sign in to continue your walk</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-neutral-600 mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-600 mb-1" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="Your password"
              />
            </div>

            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-neutral-900 hover:bg-neutral-700 text-white font-medium py-3 transition disabled:opacity-60"
            >
              {loading ? "One moment…" : "Sign in"}
            </button>
          </form>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="mt-3 w-full rounded-full border border-neutral-300 py-3 text-sm font-medium text-neutral-900 transition hover:border-neutral-900"
          >
            Continue with Google
          </button>

          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-neutral-500">
              New here?{" "}
              <Link to="/sign-up" className="text-neutral-900 underline underline-offset-4 hover:text-neutral-600">
                Create an account
              </Link>
            </p>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
